import React from 'react';
import { View, Text, Image, TouchableOpacity, TextInput } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

// Types matching ComponentDesigner
interface ComponentElement {
  id: string;
  type: 'text' | 'header' | 'image' | 'input' | 'button' | 'container' | 'divider';
  x: number;
  y: number;
  width: number;
  height: number;
  properties: {
    text?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
    color?: string;
    backgroundColor?: string;
    borderRadius?: number;
    padding?: number;
    margin?: number;
    alignment?: 'left' | 'center' | 'right';
    dataBinding?: string;
    placeholder?: string;
    imageSource?: string;
    borderWidth?: number;
    borderColor?: string;
    height?: number;
  };
}

interface CustomView {
  id: string;
  name: string;
  dataId: string;
  elements: ComponentElement[];
  canvasSettings: {
    width: number;
    height: number;
    backgroundColor: string;
    gridSize: number;
    showGrid: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

interface CustomViewRendererProps {
  customView: CustomView;
  dataItem: any; // The actual data item to render
  onElementPress?: (elementId: string, element: ComponentElement) => void;
  theme?: boolean;
  containerStyle?: any;
}

const CustomViewRenderer: React.FC<CustomViewRendererProps> = ({
  customView,
  dataItem,
  onElementPress,
  theme = true,
  containerStyle
}) => {
  // Function to resolve data binding
  const resolveDataBinding = (element: ComponentElement): string => {
    if (!element.properties.dataBinding || !dataItem) {
      return element.properties.text || element.properties.placeholder || '';
    }

    try {
      // Handle nested object properties (e.g., "customer.name", "profile.email")
      const keys = element.properties.dataBinding.split('.');
      let value = dataItem;

      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return element.properties.text || element.properties.placeholder || '';
        }
      }

      // Convert value to string
      return value?.toString() || '';
    } catch (error) {
      console.warn('Error resolving data binding:', error);
      return element.properties.text || element.properties.placeholder || '';
    }
  };

  // Function to resolve image data binding
  const resolveImageBinding = (element: ComponentElement): string => {
    if (!element.properties.dataBinding || !dataItem) {
      return element.properties.imageSource || 'https://via.placeholder.com/100';
    }

    try {
      const keys = element.properties.dataBinding.split('.');
      let value = dataItem;

      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return element.properties.imageSource || 'https://via.placeholder.com/100';
        }
      }

      // Handle array of images (take first one)
      if (Array.isArray(value) && value.length > 0) {
        return value[0];
      }

      return value?.toString() || element.properties.imageSource || 'https://via.placeholder.com/100';
    } catch (error) {
      return element.properties.imageSource || 'https://via.placeholder.com/100';
    }
  };

  // Render individual element based on type
  const renderElement = (element: ComponentElement) => {
    const commonStyle = {
      position: 'absolute' as const,
      left: element.x,
      top: element.y,
      width: element.width,
      height: element.height,
    };

    const textAlign = element.properties.alignment === 'center' ? 'center' :
      element.properties.alignment === 'right' ? 'right' : 'left';

    switch (element.type) {
      case 'text':
        return (
          <View key={element.id} style={commonStyle}>
            <TouchableOpacity
              onPress={() => onElementPress?.(element.id, element)}
              disabled={!onElementPress}
              style={{ flex: 1 }}
            >
              <Text
                style={{
                  fontSize: element.properties.fontSize || 16,
                  fontWeight: element.properties.fontWeight || 'normal',
                  color: element.properties.color || (theme ? '#000000' : '#ffffff'),
                  textAlign: textAlign as any,
                  padding: element.properties.padding || 0,
                  margin: element.properties.margin || 0,
                }}
              >
                {resolveDataBinding(element)}
              </Text>
            </TouchableOpacity>
          </View>
        );

      case 'header':
        return (
          <View key={element.id} style={commonStyle}>
            <TouchableOpacity
              onPress={() => onElementPress?.(element.id, element)}
              disabled={!onElementPress}
              style={{ flex: 1 }}
            >
              <Text
                style={{
                  fontSize: element.properties.fontSize || 24,
                  fontWeight: element.properties.fontWeight || 'bold',
                  color: element.properties.color || (theme ? '#000000' : '#ffffff'),
                  textAlign: textAlign as any,
                  padding: element.properties.padding || 0,
                  margin: element.properties.margin || 0,
                }}
              >
                {resolveDataBinding(element)}
              </Text>
            </TouchableOpacity>
          </View>
        );

      case 'image':
        return (
          <View key={element.id} style={commonStyle}>
            <TouchableOpacity
              onPress={() => onElementPress?.(element.id, element)}
              disabled={!onElementPress}
              style={{ flex: 1 }}
            >
              <Image
                source={{ uri: resolveImageBinding(element) }}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: element.properties.borderRadius || 0,
                  margin: element.properties.margin || 0,
                }}
                resizeMode="cover"
              />
            </TouchableOpacity>
          </View>
        );

      case 'input':
        return (
          <View key={element.id} style={commonStyle}>
            <TextInput
              value={resolveDataBinding(element)}
              placeholder={element.properties.placeholder}
              style={{
                width: '100%',
                height: '100%',
                fontSize: element.properties.fontSize || 16,
                padding: element.properties.padding || 12,
                borderWidth: element.properties.borderWidth || 1,
                borderColor: element.properties.borderColor || '#cccccc',
                borderRadius: element.properties.borderRadius || 8,
                backgroundColor: element.properties.backgroundColor || (theme ? '#ffffff' : '#333333'),
                color: element.properties.color || (theme ? '#000000' : '#ffffff'),
                textAlign: textAlign as any,
                margin: element.properties.margin || 0,
              }}
              editable={false} // Read-only in view mode
            />
          </View>
        );

      case 'button':
        return (
          <View key={element.id} style={commonStyle}>
            <TouchableOpacity
              onPress={() => onElementPress?.(element.id, element)}
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: element.properties.backgroundColor || '#2196F3',
                borderRadius: element.properties.borderRadius || 8,
                padding: element.properties.padding || 12,
                margin: element.properties.margin || 0,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text
                style={{
                  fontSize: element.properties.fontSize || 16,
                  fontWeight: element.properties.fontWeight || 'bold',
                  color: element.properties.color || '#ffffff',
                  textAlign: 'center',
                }}
              >
                {resolveDataBinding(element)}
              </Text>
            </TouchableOpacity>
          </View>
        );

      case 'container':
        return (
          <View
            key={element.id}
            style={[
              commonStyle,
              {
                backgroundColor: element.properties.backgroundColor || 'transparent',
                borderWidth: element.properties.borderWidth || 0,
                borderColor: element.properties.borderColor || 'transparent',
                borderRadius: element.properties.borderRadius || 0,
                padding: element.properties.padding || 0,
                margin: element.properties.margin || 0,
              }
            ]}
          >
            <TouchableOpacity
              onPress={() => onElementPress?.(element.id, element)}
              disabled={!onElementPress}
              style={{ flex: 1 }}
            />
          </View>
        );

      case 'divider':
        return (
          <View
            key={element.id}
            style={[
              commonStyle,
              {
                height: element.properties.height || 2,
                backgroundColor: element.properties.backgroundColor || (theme ? '#e0e0e0' : '#444444'),
                margin: element.properties.margin || 8,
              }
            ]}
          />
        );

      default:
        return null;
    }
  };

  const canvasStyle = {
    width: customView.canvasSettings.width,
    height: customView.canvasSettings.height,
    backgroundColor: customView.canvasSettings.backgroundColor,
    position: 'relative' as const,
    overflow: 'hidden' as const,
  };

  return (
    <View style={[canvasStyle, containerStyle]}>
      {customView.elements.map(renderElement)}
    </View>
  );
};

export default CustomViewRenderer;